/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import strings from 'strings';
import { trackEvent } from 'helpers/analytics';
import { updateJobSourcingCampaign } from 'hooks/useJobs/actions';
import { activeNetworkSelector, userSelector } from '../../../../redux/selectors';
import { useCreateSourcingCampaignMutation } from '../../../../services/findemV2';
import { getUserDisplayName } from '../../../../helpers/users';

export const APPLICATION_BOOST_CREDITS = 20;
export const CALIBRATION_AGENT_CREDITS = 10;

export const useAgentBoost = ({ job, openWidget, setWidgetView, widgetView }) => {
  const user = useSelector(userSelector);
  const [isCalibrationAgentSelected, setIsCalibrationAgentSelected] = useState(false);

  const [createSourcingCampaign, { isLoading: isCreateSourcingCampaignLoading }] = useCreateSourcingCampaignMutation();

  const totalCredits = APPLICATION_BOOST_CREDITS + (isCalibrationAgentSelected ? CALIBRATION_AGENT_CREDITS : 0);
  const activeNetwork = useSelector(activeNetworkSelector);
  const availableAgentCredits = activeNetwork?.availableAgentCredits || 0;
  const dispatch = useDispatch();

  useEffect(() => {
    if (availableAgentCredits < totalCredits) {
      trackEvent('application_boost:not_enough_credits');
    }
  }, []);

  const handleStartAgent = async () => {
    try {
      const sourcingCampaign = await createSourcingCampaign({
        jobId: job?.id,
        calibrate: isCalibrationAgentSelected,
      }).unwrap();

      dispatch(
        updateJobSourcingCampaign({
          jobId: job?.id,
          sourcingCampaign,
        }),
      );

      trackEvent('application_boost:boost_bought', {
        job_id: job?.id,
        company_id: job?.organization?.id,
        with_calibration: isCalibrationAgentSelected,
      });

      trackEvent('application_boost:start_calibration_click', {
        job_id: job?.id,
        company_id: job?.organization?.id,
        with_calibration: isCalibrationAgentSelected,
      });

      // If calibration is selected, go to calibration selection step
      if (isCalibrationAgentSelected) {
        // Create campaign first to get campaign ID for calibration link
        // setCampaignId(sourcingCampaign?.id || sourcingCampaign?.campaignId || null);
        openWidget({
          view: 'calibration-selection',
          job: {
            ...job,
            sourcingCampaign,
          },
        });
        return;
      }

      openWidget({
        view: 'boost',
        job: {
          ...job,
          sourcingCampaign,
        },
      });
    } catch (error) {
      toast(error?.data?.error ?? strings.genericError, {
        icon: 'danger',
        id: 'start-agent-error',
      });
    }
  };

  const handleStartCalibration = async () => {
    try {
      trackEvent('application_boost:boost_bought', {
        job_id: job?.id,
        company_id: job?.organizationId,
        with_calibration: isCalibrationAgentSelected,
      });

      trackEvent('application_boost:start_calibration_click', {
        job_id: job?.id,
        company_id: job?.organization?.id,
        with_calibration: isCalibrationAgentSelected,
      });
      openWidget({ view: 'calibration', job });
    } catch (error) {
      toast('Error starting calibration', {
        icon: 'danger',
        id: 'start-calibration-error',
      });
    }
  };

  const handleCalibrationToggle = () => {
    setIsCalibrationAgentSelected(!isCalibrationAgentSelected);
  };

  const companyName = job?.organization?.name || '[Company]';
  const jobTitle = job?.title || '[Job Title]';
  const userFullName = getUserDisplayName(user, false) || user?.email || '[Current user full name]';

  const modalVariant = useMemo(() => {
    let variant = 'default';

    if (widgetView === 'boost') {
      variant = 'large';
    }
    if (widgetView === 'calibration') {
      variant = 'fullscreen';
    }
    if (widgetView === 'calibration-selection') {
      variant = 'default';
    }
    return variant;
  }, [widgetView]);

  return {
    isCalibrationAgentSelected,
    setIsCalibrationAgentSelected,
    isLoading: isCreateSourcingCampaignLoading,
    handleStartAgent,
    handleStartCalibration,
    handleCalibrationToggle,
    companyName,
    jobTitle,
    userFullName,
    collectionId: activeNetwork?.id,
    jobId: job?.id,
    modalVariant,
    user,
    totalCredits,
    cleanFindemWidget: () => {
      setWidgetView({ view: 'launch', job: null });
    },
    availableAgentCredits,
  };
};
